import { motion } from 'framer-motion';
import { ArrowRight, TrendingUp, Shield, Zap, Sparkles } from 'lucide-react';

interface HeroProps {
  onStartPredict: () => void;
}

export default function Hero({ onStartPredict }: HeroProps) {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-16 px-4">
      <div className="max-w-6xl mx-auto text-center">
        {/* 标签 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyber-purple/10 border border-cyber-purple/30 mb-8"
        >
          <Sparkles className="w-4 h-4 text-cyber-purple" />
          <span className="text-sm text-cyber-purple">基于 Kronos AI 模型</span>
        </motion.div>

        {/* 主标题 */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-display text-5xl md:text-7xl font-black mb-6"
        >
          <span className="bg-gradient-to-r from-cyber-purple via-cyber-blue to-cyber-green bg-clip-text text-transparent">
            加密货币
          </span>
          <br />
          <span className="text-white">走势预测</span>
        </motion.h1>

        {/* 副标题 */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-xl text-gray-400 max-w-2xl mx-auto mb-8"
        >
          使用 <span className="text-cyber-blue">x402 协议</span> 进行微支付，
          获取基于 AI 的专业级加密货币价格预测
        </motion.p>

        {/* 价格标签 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl glass border border-cyber-green/30 mb-12"
        >
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-cyber-green animate-pulse" />
            <span className="text-gray-400">单次查询</span>
          </div>
          <span className="font-display text-2xl font-bold text-cyber-green">
            0.01 MON
          </span>
        </motion.div>

        {/* CTA 按钮 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <button
            onClick={onStartPredict}
            className="btn-cyber group flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-cyber-purple to-cyber-blue text-white font-bold text-lg shadow-lg shadow-cyber-purple/30 hover:shadow-cyber-purple/50 transition-all"
          >
            开始预测
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          
          <a
            href="#how-it-works"
            className="flex items-center gap-2 px-6 py-4 rounded-xl border border-gray-700 text-gray-300 hover:border-cyber-purple/50 hover:text-white transition-all"
          >
            了解更多
          </a>
        </motion.div>

        {/* 特性卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <FeatureCard
            icon={<TrendingUp className="w-6 h-6" />}
            title="AI 驱动预测"
            description="基于 Kronos 模型，分析 45+ 交易所数据"
            color="purple"
          />
          <FeatureCard
            icon={<Shield className="w-6 h-6" />}
            title="x402 安全支付"
            description="去中心化微支付，无需信任第三方"
            color="blue"
          />
          <FeatureCard
            icon={<Zap className="w-6 h-6" />}
            title="Monad 高性能"
            description="毫秒级确认，极低 Gas 费用"
            color="green"
          />
        </motion.div>
      </div>

      {/* 装饰元素 */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-cyber-darker to-transparent pointer-events-none" />
    </section>
  );
}

function FeatureCard({ 
  icon, 
  title, 
  description, 
  color 
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  color: 'purple' | 'blue' | 'green';
}) {
  const colors = {
    purple: 'from-cyber-purple/20 border-cyber-purple/30 text-cyber-purple',
    blue: 'from-cyber-blue/20 border-cyber-blue/30 text-cyber-blue',
    green: 'from-cyber-green/20 border-cyber-green/30 text-cyber-green',
  };

  return (
    <div className={`p-6 rounded-2xl bg-gradient-to-br ${colors[color]} to-transparent border backdrop-blur-sm`}>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center mb-4`}>
        {icon}
      </div>
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm">{description}</p>
    </div>
  );
}

